import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import SellerLayout from "../../components/SellerLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { FileDown, FileSpreadsheet, DollarSign, Loader2 } from "lucide-react";
import B2CReportsSection from "@/components/B2CReportsSection";
import DateRangePicker, { defaultRange } from "@/components/DateRangePicker";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DATE_FIELD_OPTIONS = [
  { value: "created_at", label: "Created At" },
  { value: "delivered_date", label: "Delivered Date" },
  { value: "manifested_date", label: "Manifested Date" },
  { value: "appointment_date", label: "Appointment Date" },
];

const SellerReports = ({ user, onLogout }) => {
  const [tab, setTab] = useState("b2c");
  // LRN Detail Report state
  const [lrnDetailDateField, setLrnDetailDateField] = useState("created_at");
  const [lrnDetailRange, setLrnDetailRange] = useState(() => defaultRange("this_month"));
  const [downloadingLrnDetail, setDownloadingLrnDetail] = useState(false);

  // LRN Charges Report state
  const [lrnChargesRange, setLrnChargesRange] = useState(() => defaultRange("this_month"));
  const [downloadingLrnCharges, setDownloadingLrnCharges] = useState(false);

  const downloadReport = async (endpoint, params, setDownloading, filename) => {
    setDownloading(true);
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      
      const response = await axios.get(`${API}/reports/${endpoint}?${queryParams.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Report downloaded successfully!");
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.error("Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadLrnDetail = () => {
    const params = {
      date_field: lrnDetailDateField,
      start_date: lrnDetailRange.start,
      end_date: lrnDetailRange.end
    };
    downloadReport("lrn-detail", params, setDownloadingLrnDetail, `lrn_detail_report_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const handleDownloadLrnCharges = () => {
    const params = {
      start_date: lrnChargesRange.start,
      end_date: lrnChargesRange.end
    };
    downloadReport("lrn-charges", params, setDownloadingLrnCharges, `lrn_charges_report_${new Date().toISOString().split('T')[0]}.csv`);
  };

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="seller-reports-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
          <p className="text-slate-600">Generate and download CSV reports for your shipments</p>
        </div>

        {/* B2C / B2B tabs */}
        <div className="flex gap-2 border-b border-slate-200">
          {[["b2c", "B2C Last Mile"], ["b2b", "B2B Freight"]].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`reports-tab-${id}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === id ? "border-brand text-brand" : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "b2c" ? (
          <B2CReportsSection />
        ) : (
        <div className="grid gap-6 md:grid-cols-2">
          {/* LRN Detail Report */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                <CardTitle className="text-lg">LRN Detail Report</CardTitle>
              </div>
              <CardDescription>
                Export detailed information for your orders including LRN, consignee details, shipping info, and dates.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Date Field</Label>
                <Select value={lrnDetailDateField} onValueChange={setLrnDetailDateField}>
                  <SelectTrigger className="mt-1" data-testid="lrn-detail-date-field">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_FIELD_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Date range</Label>
                <div className="mt-1">
                  <DateRangePicker value={lrnDetailRange} onChange={setLrnDetailRange} />
                </div>
              </div>

              <Button
                onClick={handleDownloadLrnDetail}
                disabled={downloadingLrnDetail || !lrnDetailRange.start || !lrnDetailRange.end}
                className="w-full"
                style={{ backgroundColor: (!lrnDetailRange.start || !lrnDetailRange.end) ? '#94a3b8' : '#00A699' }}
                data-testid="download-lrn-detail-btn"
              >
                {downloadingLrnDetail ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><FileDown className="w-4 h-4 mr-2" /> Download</>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* LRN Charges Report */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                <CardTitle className="text-lg">LRN Charges Report</CardTitle>
              </div>
              <CardDescription>
                Export charges breakdown for your orders including freight, COD, handling, and appointment charges.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-md">
                <p className="text-sm text-slate-600">
                  This report shows all charges applied to your orders with detailed breakdown.
                </p>
              </div>
              
              <div>
                <Label>Date range</Label>
                <div className="mt-1">
                  <DateRangePicker value={lrnChargesRange} onChange={setLrnChargesRange} />
                </div>
              </div>

              <Button
                onClick={handleDownloadLrnCharges}
                disabled={downloadingLrnCharges || !lrnChargesRange.start || !lrnChargesRange.end}
                className="w-full"
                style={{ backgroundColor: (!lrnChargesRange.start || !lrnChargesRange.end) ? '#94a3b8' : '#00A699' }}
                data-testid="download-lrn-charges-btn"
              >
                {downloadingLrnCharges ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><FileDown className="w-4 h-4 mr-2" /> Download</>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
        )}
      </div>
    </SellerLayout>
  );
};

export default SellerReports;
